/** @jsxImportSource theme-ui */
import React, { useState } from "react"
import _ from "lodash"
import { useTranslation } from "react-i18next"
import { useMutation } from "react-relay"
import CreatableSelect from "react-select/creatable"

import TextField from "../../Radiate/TextField/TextField"
import BoxButton from "../../Radiate/NewBoxButton/NewBoxButton"
import { toast } from "../../Radiate/Toaster/Toaster"
import { UpdateOutboundHookConfigMutation } from "../SettingsModalQuery"
import { errorMessage, networkErrorMessage } from "./hookErrors"
import * as Styles from "./styles"

// Backend defaults (see outboundHookSandbox.js / outboundHookFetch.js). Shown in
// the UI so admins know what applies when a field is left blank.
const DEFAULT_STEP_TIMEOUT_MS = 1000
const DEFAULT_CHAIN_TIMEOUT_MS = 5000
// Mirror of the backend OUTBOUND_HOOK_GLOBAL_FETCH_ALLOWLIST — the always-on
// hosts, shown as fixed tags. Comma-separated; defaults to the platform host.
const PLATFORM_DEFAULT_HOSTS = (
  process.env.REACT_APP_OUTBOUND_HOOK_GLOBAL_FETCH_ALLOWLIST || "open.api.woztell.com"
)
  .split(",")
  .map((h) => h.trim())
  .filter(Boolean)

// Mirror of the backend check: a bare hostname has no scheme, path, port,
// whitespace or userinfo. Fail fast in the UI before sending.
export const isBareHostname = (raw) => {
  const host = (raw || "").trim()
  if (!host) return false
  if (/[\s/@]/.test(host)) return false
  if (host.includes("://")) return false
  if (host.includes(":")) return false // no port
  return /^[a-z0-9.-]+$/i.test(host)
}

// "" -> null (use server default); valid non-negative int -> number; invalid -> NaN
const parseTimeout = (raw) => {
  const s = (raw ?? "").toString().trim()
  if (s === "") return null
  if (!/^\d+$/.test(s)) return NaN
  return parseInt(s, 10)
}

const AppConfigSection = ({ app, canEdit }) => {
  const { t } = useTranslation(["settings", "common"])
  const [updateConfig, saving] = useMutation(UpdateOutboundHookConfigMutation)

  const initial = app?.outboundHookConfig || {}
  const [stepTimeout, setStepTimeout] = useState(
    initial.stepTimeoutMs != null ? String(initial.stepTimeoutMs) : ""
  )
  const [chainTimeout, setChainTimeout] = useState(
    initial.chainTimeoutMs != null ? String(initial.chainTimeoutMs) : ""
  )
  const [hosts, setHosts] = useState(initial.fetchAllowlistHosts || [])
  const [error, setError] = useState("")

  const stepVal = parseTimeout(stepTimeout)
  const chainVal = parseTimeout(chainTimeout)
  const stepInvalid = Number.isNaN(stepVal)
  const chainInvalid = Number.isNaN(chainVal)

  const dirty =
    stepTimeout !== (initial.stepTimeoutMs != null ? String(initial.stepTimeoutMs) : "") ||
    chainTimeout !== (initial.chainTimeoutMs != null ? String(initial.chainTimeoutMs) : "") ||
    !_.isEqual(hosts, initial.fetchAllowlistHosts || [])

  // The platform default host is always reachable and is shown as a fixed,
  // non-removable tag; the user only manages the *additional* hosts.
  const hostOptions = [
    ...PLATFORM_DEFAULT_HOSTS.map((h) => ({ value: h, label: h, isFixed: true })),
    ...hosts.map((h) => ({ value: h, label: h })),
  ]

  const onHostsChange = (newValue, actionMeta) => {
    // Never let the fixed platform-default host be removed or cleared.
    if (
      (actionMeta.action === "remove-value" || actionMeta.action === "pop-value") &&
      actionMeta.removedValue?.isFixed
    ) {
      return
    }
    if (actionMeta.action === "clear") {
      setHosts([])
      return
    }
    setHosts((newValue || []).filter((v) => !v.isFixed).map((v) => v.value))
  }

  // Only allow creating a tag that is a valid, non-duplicate bare hostname.
  const isValidNewOption = (input) => {
    const v = (input || "").trim()
    return isBareHostname(v) && !PLATFORM_DEFAULT_HOSTS.includes(v) && !hosts.includes(v)
  }

  const selectStyles = {
    control: (base) => ({
      ...base,
      fontSize: "0.85rem",
      backgroundColor: !canEdit ? "#f8f8f8" : base.backgroundColor,
    }),
    menu: (base) => ({ ...base, fontSize: "0.85rem" }),
    multiValue: (base, state) =>
      (state.data.isFixed ? { ...base, backgroundColor: "#edf2f7" } : base),
    multiValueLabel: (base, state) =>
      (state.data.isFixed ? { ...base, color: "#4a5568", fontStyle: "italic" } : base),
    multiValueRemove: (base, state) =>
      (state.data.isFixed ? { ...base, display: "none" } : base),
  }

  const handleSave = () => {
    setError("")
    if (stepInvalid || chainInvalid) {
      setError(t("outbound_hooks.config.timeout_invalid", "Timeouts must be non-negative whole numbers (in ms)."))
      return
    }
    let clientMutationId = 0
    updateConfig({
      variables: {
        input: {
          config: {
            stepTimeoutMs: stepVal,
            chainTimeoutMs: chainVal,
            fetchAllowlistHosts: hosts,
          },
          clientMutationId: clientMutationId++,
        },
      },
      onCompleted: (response) => {
        const err = response?.updateOutboundHookConfig?.error
        if (err) {
          setError(errorMessage(err, t("common:errors.something_went_wrong", "Something went wrong.")))
          return
        }
        toast.success(t("outbound_hooks.config.saved", "Execution settings saved"))
      },
      onError: (e) => {
        setError(networkErrorMessage(e, t("common:errors.something_went_wrong", "Something went wrong.")))
      },
    })
  }

  return (
    <div>
      <Styles.FieldRow>
        <TextField
          useInput
          type="number"
          borderStyle="M"
          label={t("outbound_hooks.config.step_timeout", "Per-step timeout (ms)")}
          placeholder={t("outbound_hooks.config.default_ms", "Default: {{ms}} ms", { ms: DEFAULT_STEP_TIMEOUT_MS })}
          value={stepTimeout}
          disabled={!canEdit}
          error={stepInvalid ? { text: t("outbound_hooks.config.must_be_number", "Must be a whole number") } : {}}
          onChange={(e) => setStepTimeout(e.target.value)}
        />
        <TextField
          useInput
          type="number"
          borderStyle="M"
          label={t("outbound_hooks.config.chain_timeout", "Total chain timeout (ms)")}
          placeholder={t("outbound_hooks.config.default_ms", "Default: {{ms}} ms", { ms: DEFAULT_CHAIN_TIMEOUT_MS })}
          value={chainTimeout}
          disabled={!canEdit}
          error={chainInvalid ? { text: t("outbound_hooks.config.must_be_number", "Must be a whole number") } : {}}
          onChange={(e) => setChainTimeout(e.target.value)}
        />
      </Styles.FieldRow>

      <div sx={{ fontSize: "14px", color: "gray9", mt: 3 }}>
        {t("outbound_hooks.config.allowlist", "Fetch host allowlist")}
      </div>
      <div sx={{ fontSize: "12px", color: "gray6", mt: 1, mb: 2 }}>
        {t(
          "outbound_hooks.config.allowlist_hint",
          "Hosts that hook fetch() calls may reach. The platform default (shown in italics) is always allowed; add bare hostnames for any others."
        )}
      </div>

      <CreatableSelect
        isMulti
        isClearable={false}
        isDisabled={!canEdit}
        value={hostOptions}
        onChange={onHostsChange}
        isValidNewOption={isValidNewOption}
        components={{ DropdownIndicator: null }}
        formatCreateLabel={(input) => t("outbound_hooks.config.add_host_label", 'Add "{{host}}"', { host: input })}
        noOptionsMessage={() =>
          t("outbound_hooks.config.host_hint", "Type a bare hostname (e.g. api.example.com) and press enter")
        }
        placeholder={t("outbound_hooks.config.host_placeholder", "api.example.com")}
        styles={selectStyles}
      />

      {error && <Styles.InlineError>{error}</Styles.InlineError>}

      {canEdit && (
        <div sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <BoxButton
            primary
            text={t("common:buttons.save", "Save")}
            loading={saving}
            disabled={!dirty || saving}
            onClick={handleSave}
          />
        </div>
      )}
    </div>
  )
}

export default AppConfigSection
